import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { IAssetConfig, IAssetsConfig } from '@ConfigLoader/Configs/AssetsConfig';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { SoundManager } from '@Managers/SoundManager/SoundManager';
import { Assets, UnresolvedAsset } from 'pixi.js';
import { GameScene } from '..';

const PRELOAD_BUNDLE_NAME = 'preload';
const LOAD_BUNDLE_NAME = 'load';

// Progress split between the two phases. Preload occupies [0, PRELOAD_PHASE_END],
// the load phase (graphics bundle + sounds, running in parallel) occupies the rest.
const PRELOAD_PHASE_END = 0.25;

type AssetEntry = IAssetsConfig['bitmapFonts']['assets'][number];

class AssetsLoadManager {
    public static readonly CONFIGURATION_BASE_URL: string;
    public static readonly ASSETS_CONFIGURATION_FILE_NAME: string = 'assets_configuration.json';
    public static readonly ASSETS_PATH: string = '/configuration/';

    public scene: GameScene;

    private isMobile: boolean;
    private isFinal: boolean;
    private assetsNode: IAssetsConfig;

    // Number of unique assets in the "load" bundle, used to weight graphics vs sounds
    // in the combined progress calculation (see loadMainPhase).
    private loadBundleAssetCount = 0;

    private readonly atlasMap: Map<string, string[]> = new Map();

    public initialize(scene: GameScene): void {
        this.scene = scene;
        this.isMobile = this.scene.game.gameStore.get(EGameStoreKeys.IsMobile);
        this.assetsNode = ConfigLoader.getConfiguration(EConfigurationNames.ASSETS);

        if (!this.assetsNode) {
            console.error('Cannot get assets configuration!');
            return;
        }

        const testingAssets = Boolean(process.env.ASSETS_TEST);
        this.isFinal = testingAssets ? false : this.assetsNode.isFinal;

        this.registerBundles();
    }

    public async loadFiles(): Promise<void> {
        EventsCenter.emit(EGameEvents.ASSET_LOADER_INFORMATION, { loaderState: 'START' });

        await this.loadFont(ConfigLoader.fontFamily.global.name, ConfigLoader.gameResourcePath + ConfigLoader.fontFamily.global.path);

        await Assets.loadBundle(PRELOAD_BUNDLE_NAME, (percent) => {
            EventsCenter.emit(EGameEvents.ASSET_LOADER_INFORMATION, { progress: percent * PRELOAD_PHASE_END });
        });
        EventsCenter.emit(EGameEvents.ASSETS_PRELOAD_FINISHED);

        await this.loadMainPhase();

        EventsCenter.emit(EGameEvents.ASSET_LOADER_INFORMATION, { loaderState: 'COMPLETE' });
        EventsCenter.emit(EGameEvents.ASSETS_LOAD_FINISHED);
    }

    public async loadPreloaderAnimation(key: string, baseUrl: string, fileName: string, callback?: any): Promise<void> {
        await Assets.load({ alias: key, src: this.generateUrl(baseUrl, fileName, false) });
        callback();
    }

    public getAtlasBySpineKey(key: string): string {
        const entry = [...this.atlasMap.entries()].find(([, spineKeys]) => spineKeys.includes(key));
        if (entry) return entry[0];
        throw Error('Atlas for spine: ' + key + ' does not exisit');
    }

    public getSpinesConfiguration(): IAssetConfig {
        const config = ConfigLoader.getConfiguration(EConfigurationNames.ASSETS);
        return this.isFinal ? config.finalSpines : config.spines;
    }

    private registerBundles(): void {
        const preloadAssets = this.collectAssets(true);
        const loadAssets = this.collectAssets(false);

        this.loadBundleAssetCount = loadAssets.length;

        Assets.addBundle(PRELOAD_BUNDLE_NAME, preloadAssets);
        Assets.addBundle(LOAD_BUNDLE_NAME, loadAssets);
    }

    private collectAssets(preload: boolean): UnresolvedAsset[] {
        const dedup = new Map<string, UnresolvedAsset>();
        const add = (entries: UnresolvedAsset[]) => entries.forEach((entry) => dedup.set(entry.alias as string, entry));

        if (this.assetsNode.fonts) add(this.buildSimpleAssets(this.assetsNode.fonts, 'ttf', preload));
        add(this.buildSimpleAssets(this.assetsNode.bitmapFonts, 'xml', preload));
        add(this.buildSimpleAssets(this.assetsNode.video, 'movie', preload));
        add(this.buildSimpleAssets(this.assetsNode.images, 'image', preload));
        add(this.buildSpritesheetAssets(this.assetsNode.spritesheets, preload));
        add(this.buildSpineAssets(this.isFinal ? this.assetsNode.finalSpines : this.assetsNode.spines, preload));

        return [...dedup.values()];
    }

    private matchesPhase(asset: AssetEntry, preload: boolean): boolean {
        return !!asset.preload === preload;
    }

    private buildSimpleAssets(config: IAssetConfig, urlField: 'xml' | 'movie' | 'image' | 'ttf', preload: boolean): UnresolvedAsset[] {
        return config.assets
            .filter((asset) => this.matchesPhase(asset, preload))
            .map((asset) => ({
                alias: asset.key,
                src: this.generateUrl(`${ConfigLoader.gameResourcePath}/${config.baseUrl}`, asset[urlField], asset.mobile && this.isMobile, config),
                data: {
                    family: urlField === 'ttf' ? asset.key : undefined
                }
            }));
    }

    private buildSpritesheetAssets(config: IAssetConfig, preload: boolean): UnresolvedAsset[] {
        return config.assets
            .filter((asset) => this.matchesPhase(asset, preload))
            .flatMap((asset) => [
                {
                    alias: asset.key + '-json',
                    src: this.generateUrl(`${ConfigLoader.gameResourcePath}/${config.baseUrl}`, asset.json, asset.mobile && this.isMobile, config)
                },
                {
                    alias: asset.key + '-image',
                    src: this.generateUrl(`${ConfigLoader.gameResourcePath}/${config.baseUrl}`, asset.image, asset.mobile && this.isMobile, config)
                }
            ]);
    }

    private buildSpineAssets(config: IAssetConfig, preload: boolean): UnresolvedAsset[] {
        return config.assets
            .filter((asset) => this.matchesPhase(asset, preload))
            .flatMap((asset) => {
                const atlasKey = asset.atlas;
                const dataKey = asset.key;

                if (!this.atlasMap.has(atlasKey)) {
                    this.atlasMap.set(atlasKey, [dataKey]);
                } else {
                    this.atlasMap.set(atlasKey, [...this.atlasMap.get(atlasKey)!, dataKey]);
                }

                return [
                    {
                        alias: dataKey,
                        src: this.generateUrl(`${ConfigLoader.gameResourcePath}/${config.baseUrl}`, asset.json, false)
                    },
                    {
                        alias: atlasKey,
                        src: this.generateUrl(`${ConfigLoader.gameResourcePath}/${config.baseUrl}`, atlasKey, asset.mobile && this.isMobile, config)
                    }
                ];
            });
    }

    private async loadMainPhase(): Promise<void> {
        const { sharedSounds, gameSounds } = ConfigLoader.getConfiguration(EConfigurationNames.SOUNDS);
        const soundsTotal = Object.keys(sharedSounds).length + Object.keys(gameSounds).length;
        const graphicsTotal = this.loadBundleAssetCount;
        const grandTotal = soundsTotal + graphicsTotal || 1;

        const graphicsWeight = graphicsTotal / grandTotal;
        const soundsWeight = soundsTotal / grandTotal;

        let graphicsProgress = 0;
        let soundsLoaded = 0;

        const reportProgress = () => {
            const soundsProgress = soundsTotal === 0 ? 1 : soundsLoaded / soundsTotal;
            const phaseProgress = graphicsProgress * graphicsWeight + soundsProgress * soundsWeight;
            const progress = PRELOAD_PHASE_END + phaseProgress * (1 - PRELOAD_PHASE_END);
            EventsCenter.emit(EGameEvents.ASSET_LOADER_INFORMATION, { progress });
        };

        const onSoundLoaded = () => {
            soundsLoaded += 1;
            reportProgress();
        };

        await Promise.all([
            Assets.loadBundle(LOAD_BUNDLE_NAME, (percent) => {
                graphicsProgress = percent;
                reportProgress();
            }),
            SoundManager.loadSounds(sharedSounds, `${ConfigLoader.sharedResourcePath}/sounds`, onSoundLoaded),
            SoundManager.loadSounds(gameSounds, `${ConfigLoader.gameResourcePath}/sounds`, onSoundLoaded)
        ]);
    }

    private async loadFont(font: string | string[], url: string): Promise<void> {
        const fonts = typeof font === 'string' ? [font] : font;

        fonts.forEach((fontName) => {
            const newFont = new FontFace(fontName, `url(${this.generateUrl('', url, false)})`);
            newFont
                .load()
                .then((loadedFont) => document.fonts.add(loadedFont))
                .catch((error) => error);
        });

        await Assets.load({ alias: font, src: this.generateUrl('', url, false) });
    }

    private generateUrl(baseUrl: string, url: string, mobileVersion?: boolean, config?: IAssetConfig, isShared?: boolean): string {
        const desktopUrl = config?.desktopUrl ?? '';
        const mobileUrl = config?.mobileUrl ?? 'mobile/';
        const prefix = mobileVersion ? mobileUrl : desktopUrl;

        return `${baseUrl ? baseUrl + '/' : ''}${isShared ? '' : prefix}${url}?v=${ConfigLoader.getConfiguration(EConfigurationNames.VERSION).cacheVersion}`;
    }
}

export default new AssetsLoadManager();
